import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { exportToPDF, exportToExcel } from '../../utils/exportUtils';
import './Report.css';

const Report = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, getStudentEnrollments, getStudentGrades, calculateECTS } = useData();

  const userId = user?.id || user?._id;
  const userName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const enrollments = getStudentEnrollments(userId);
  const grades = getStudentGrades(userId);

  const courseMetaById = useMemo(() => {
    const map = new Map();
    (data.studies || []).forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((moduleDoc) => {
          (moduleDoc.courses || []).forEach((course) => {
            map.set(String(course.id || course._id), {
              studyName: study.name,
              yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`,
              moduleName: moduleDoc.name
            });
          });
        });
      });
    });
    return map;
  }, [data.studies]);

  const enrollmentCards = useMemo(() => {
    return enrollments.map((enrollment) => {
      const courseId = String(enrollment.course?.id || enrollment.course?._id || enrollment.courseId || '');
      const meta = courseMetaById.get(courseId) || {};
      return {
        ...enrollment,
        studyName: meta.studyName || 'Onbekende studie',
        yearLabel: meta.yearLabel || 'Onbekend studiejaar',
        moduleName: meta.moduleName || 'Onbekende module'
      };
    });
  }, [enrollments, courseMetaById]);

  const cohortLabel = user?.cohort?.name || user?.cohort?.code || 'Geen cohort';

  // Calculate overall progress
  const calculateOverallProgress = () => {
    if (enrollments.length === 0) return 0;
    const passedCourses = new Set(
      grades
        .filter(g => g.status === 'passed')
        .map(g => String(g.courseId || g.course?.id))
    );
    return Math.round((passedCourses.size / enrollments.length) * 100);
  };

  const overallProgress = calculateOverallProgress();
  const totalEcts = calculateECTS(userId);

  const gradeExportData = grades.map(grade => {
    const status = grade.status === 'passed' ? 'Passed' : 'Failed';

    return {
      Course: grade.course?.name || 'Unknown',
      Code: grade.course?.code || '-',
      Score: grade.grade,
      Status: status,
      Date: grade.gradeDate || ''
    };
  });

  const handleExportPdf = () => {
    exportToPDF(
      gradeExportData,
      `NLBA_Report_${userName}`,
      `${t('student.report')} - ${userName}`
    );
  };

  const handleExportExcel = () => {
    exportToExcel(
      gradeExportData,
      `NLBA_Report_${userName}`,
      'Grades',
      `${t('student.report')} - ${userName}`
    );
  };

  return (
    <div className="report-page">
      <div className="page-header">
        <h1>{t('student.report')}</h1>
        <span className="student-location">{user?.location || '-'}</span>
      </div>

      <div className="report-header">
        <div className="student-info">
          <h2>{userName}</h2>
          <p>{user.email}</p>
          <div className="course-label-row" style={{ marginTop: '8px' }}>
            <span className="meta-chip">Cohort: {cohortLabel}</span>
          </div>
        </div>
        <div className="overall-stats">
          <div className="stat-card">
            <span className="stat-value">{overallProgress}%</span>
            <span className="stat-label">{t('progress.overall')}</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{totalEcts}</span>
            <span className="stat-label">{t('student.ectsEarned')}</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{enrollments.length}</span>
            <span className="stat-label">{t('portal.enrolledCourses')}</span>
          </div>
        </div>
      </div>

      <div className="progress-section">
        <h3>{t('progress.overall')}</h3>
        <div className="large-progress-bar">
          <div className="progress-fill" style={{ width: `${overallProgress}%` }}>
            {overallProgress}%
          </div>
        </div>
      </div>

      <div className="study-years-section">
        <h3>{t('portal.myCourses')}</h3>
        {enrollments.length === 0 ? (
          <p className="no-data">{t('common.noData')}</p>
        ) : (
          <div className="grades-grid">
            {enrollmentCards.map((enrollment) => (
              <div key={enrollment.id} className="grade-card">
                <div className="grade-header">
                  <span className="grade-value">{enrollment.course?.code || '-'}</span>
                  <span className={`status ${enrollment.status}`}>{enrollment.status}</span>
                </div>
                <p className="course-name">{enrollment.course?.name || '-'}</p>
                <p className="course-code">{enrollment.course?.location || '-'}</p>
                <div className="course-label-row">
                  <span className="meta-chip">{enrollment.studyName}</span>
                  <span className="meta-chip muted">{enrollment.yearLabel}</span>
                </div>
                <div className="course-label-row">
                  <span className="meta-chip muted">{enrollment.moduleName}</span>
                  <span className="meta-chip muted">{cohortLabel}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grades-overview">
        <h3>{t('grade.title')}</h3>
        
        <div className="grades-grid">
          {grades.map((grade, index) => {
            return (
              <div key={index} className="grade-card">
                <div className="grade-header">
                  <span className={`grade-value ${grade.status === 'passed' ? 'passed' : 'failed'}`}>
                    {typeof grade.grade === 'number' ? `${grade.grade.toFixed(1)}/10` : '-'}
                  </span>
                  <span className={`status ${grade.status}`}>
                    {t(`grade.${grade.status}`)}
                  </span>
                </div>
                <p className="course-name">{grade.course?.name || 'Unknown Course'}</p>
                <p className="course-code">{grade.course?.code}</p>
              </div>
            );
          })}
        </div>
        
        {grades.length === 0 && (
          <p className="no-data">{t('common.noData')}</p>
        )}
      </div>

      <div className="print-section">
        <button className="print-button" onClick={() => window.print()}>
          🖨️ {t('common.print')} {t('student.report')}
        </button>
        <button className="export-button" onClick={handleExportPdf}>
          📄 {t('common.export')} PDF
        </button>
        <button className="export-button" onClick={handleExportExcel}>
          📊 {t('common.export')} XLSX
        </button>
      </div>
    </div>
  );
};

export default Report;
